import React, { useState, useRef, useEffect, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import useViewport from 'src/hooks/useViewport'
import Slider from 'react-slick'
import Carousel from 'src/Components/shared/Carousel'
import { PreviousArrow } from '../PreviousArrow/PreviousArrow'
import { NextArrow } from '../NextArrow/NextArrow'
import { SlideOnMount } from 'src/Components/shared/Animations'
import { MlsLogo } from 'src/Components/homeSearch/Compliance/MlsLogo'
import { ListingDetailsListing } from 'src/types/listings'
import styles from './Content.module.scss'

// Constants
import { KEY_CODES } from 'src/constants/utility'

export interface ContentProps {
  listing: ListingDetailsListing
  slidesToShow?: number
  slidesToScroll?: number
  className?: string
  imageChange?: (current: number) => void
}

const Contents = ({
  listing,
  slidesToShow = 1,
  slidesToScroll = 1,
  imageChange,
}: ContentProps) => {
  const { isLargeOrAbove } = useViewport()
  const [imageIndex, setImageIndex] = useState(0)

  const sliderRef = useRef<Slider>(null)

  const next = () => sliderRef.current?.slickNext()
  const previous = () => sliderRef.current?.slickPrev()

  const handleArrowKeydown = useCallback(e => {
    if ((e.which || e.keyCode) === KEY_CODES.rightArrow) {
      next()
    } else if ((e.which || e.keyCode) === KEY_CODES.leftArrow) {
      previous()
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleArrowKeydown)

    return () => {
      document.removeEventListener('keydown', handleArrowKeydown)
    }
  }, [])

  const generateListingSlides = () =>
    listing.thumbnailImageFileNames?.map(image => (
      <img src={image} className={styles.image} alt='listing' />
    ))

  const changeImage = ({ current }: { current: number }) => {
    if (imageChange) {
      imageChange(current)
    }
    setImageIndex(current)
  }

  const slides = generateListingSlides()

  return (
    <SlideOnMount className={styles.animation} interval={50}>
      <div className={styles.carouselWrapper}>
        <Carousel
          afterChange={current => changeImage({ current })}
          arrows={isLargeOrAbove}
          infinite
          draggable
          initialSlide={0}
          nextArrow={<NextArrow />}
          prevArrow={<PreviousArrow />}
          sliderRef={sliderRef}
          slides={slides}
          slidesToScroll={slidesToScroll}
          slidesToShow={slidesToShow}
          speed={300}
          swipeToSlide
        />
      </div>
      <div className={styles.mlsLogoWrapper}>
        <MlsLogo listing={listing} />
      </div>
      <div className={styles.mobileArrowButtonContainer}>
        <PreviousArrow onClick={previous} />
        <span className={styles.imageIndex} data-qa='image-index'>
          <FormattedMessage
            id='HomeSearch.listingDetails.imageIndex'
            defaultMessage='{index} of {total}'
            values={{
              index: imageIndex + 1,
              total: listing.thumbnailImageFileNames?.length,
            }}
          />
        </span>
        <NextArrow onClick={next} />
      </div>
    </SlideOnMount>
  )
}

export default Contents
